import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  ArrowBack as BackIcon,
  Instagram as InstagramIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  FilterAlt as FilterIcon
} from '@mui/icons-material';
import api from '../../config/api';

// Post-onboarding Facebook Pages management screen.
// Reads the company's pool of FB pages (unioned from credentials_v2 across all
// active FB connectors) and lets the user assign each to a brand, re-assign
// across brands, or unmap entirely. Does not touch downloaded social-media data
// in facebook.* — only writes to facebook.page_brand_mapping.

const SKIP_VALUE = '__skip__';

const FacebookPagesManager = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hintBrandId = searchParams.get('brand_id');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [pages, setPages] = useState([]);
  const [brands, setBrands] = useState([]);
  const [connectorCount, setConnectorCount] = useState(0);

  // page_id -> brand_id (number) or SKIP_VALUE
  const [pageBrandMapping, setPageBrandMapping] = useState({});
  // Original snapshot so we know which rows to send to the save endpoint.
  const [initialMapping, setInitialMapping] = useState({});
  const [selectedPageIds, setSelectedPageIds] = useState(new Set());
  const [bulkBrand, setBulkBrand] = useState('');
  const [filterText, setFilterText] = useState('');
  const [filterBrand, setFilterBrand] = useState('all'); // 'all' | brand_id | 'unmapped'

  useEffect(() => {
    loadCompanyPages();
  }, []);

  const loadCompanyPages = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/connectors/facebook/company-pages');
      if (!response.data.success) {
        setError(response.data.message || 'Failed to load Facebook pages');
        return;
      }

      const loadedPages = response.data.pages || [];
      setPages(loadedPages);
      setBrands(response.data.brands || []);
      setConnectorCount(response.data.total_connectors || 0);

      const startMapping = {};
      loadedPages.forEach(p => {
        startMapping[p.page_id] = p.current_brand_id || SKIP_VALUE;
      });
      setPageBrandMapping(startMapping);
      setInitialMapping(startMapping);
    } catch (err) {
      console.error('Error loading company FB pages:', err);
      setError(err.response?.data?.message || 'Failed to load Facebook pages');
    } finally {
      setLoading(false);
    }
  };

  const handlePageBrandChange = (pageId, value) => {
    setPageBrandMapping(prev => ({ ...prev, [pageId]: value }));
  };

  const handlePageCheckbox = (pageId, checked) => {
    setSelectedPageIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(pageId); else next.delete(pageId);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPageIds(new Set(filteredPages.map(p => p.page_id)));
    } else {
      setSelectedPageIds(new Set());
    }
  };

  const applyBulkAssignment = (value) => {
    if (selectedPageIds.size === 0) return;
    setPageBrandMapping(prev => {
      const next = { ...prev };
      selectedPageIds.forEach(pid => { next[pid] = value; });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Only send entries that actually changed vs initial state.
      const mappings = [];
      for (const page of pages) {
        const current = pageBrandMapping[page.page_id];
        const initial = initialMapping[page.page_id];
        if (current === initial) continue;

        mappings.push({
          page_id: page.page_id,
          page_name: page.page_name,
          ig_user_id: page.ig_user_id || '',
          brand_id: current === SKIP_VALUE ? null : parseInt(current)
        });
      }

      if (mappings.length === 0) {
        setSuccess('No changes to save.');
        return;
      }

      const response = await api.post('/api/connectors/facebook/company-pages/save', { mappings });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save Facebook page mappings');
      }

      setSuccess(`Saved: ${response.data.inserted} mapped, ${response.data.removed} unmapped${response.data.rejected?.length ? `, ${response.data.rejected.length} rejected` : ''}.`);
      // Refresh from server so subsequent edits use the new baseline.
      await loadCompanyPages();
      setSelectedPageIds(new Set());
    } catch (err) {
      console.error('Error saving FB page mappings:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save Facebook page mappings');
    } finally {
      setSaving(false);
    }
  };

  const getBrandName = (id) => {
    const b = brands.find(br => parseInt(br.id) === parseInt(id));
    return b ? b.name : null;
  };

  // Filtered view: by search text + brand filter.
  const filteredPages = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return pages.filter(p => {
      if (q) {
        const hay = `${p.page_name || ''} ${p.page_id} ${p.ig_user_id || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const current = pageBrandMapping[p.page_id];
      if (filterBrand === 'all') return true;
      if (filterBrand === 'unmapped') return !current || current === SKIP_VALUE;
      return current && current !== SKIP_VALUE && parseInt(current) === parseInt(filterBrand);
    });
  }, [pages, filterText, filterBrand, pageBrandMapping]);

  const changedCount = useMemo(() => {
    let n = 0;
    for (const page of pages) {
      if (pageBrandMapping[page.page_id] !== initialMapping[page.page_id]) n++;
    }
    return n;
  }, [pages, pageBrandMapping, initialMapping]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading Facebook Pages...</Typography>
      </Box>
    );
  }

  const allFilteredChecked = filteredPages.length > 0 && filteredPages.every(p => selectedPageIds.has(p.page_id));
  const someFilteredChecked = filteredPages.some(p => selectedPageIds.has(p.page_id)) && !allFilteredChecked;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/brands')} sx={{ mr: 2 }}>
          Back to brands
        </Button>
        <FacebookIcon sx={{ color: '#1877F2', mr: 2, fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Facebook Pages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pages.length} Page(s) across {connectorCount} Meta connector(s).
            {hintBrandId && getBrandName(hintBrandId) && (
              <> Hint: filtering for brand <strong>{getBrandName(hintBrandId)}</strong>.</>
            )}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Assign Pages to Brands</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            One Page → one brand. Pages set to <strong>Skip</strong> are removed from
            tracking (mapping row is deleted — downloaded data in facebook.* is preserved).
            Switching a Page to a different brand re-tags it for future ingestion.
          </Typography>

          {/* Filter + bulk toolbar */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search by page name or ID"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="filter-brand-label">Show</InputLabel>
              <Select
                labelId="filter-brand-label"
                label="Show"
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
                startAdornment={<FilterIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />}
              >
                <MenuItem value="all">All pages</MenuItem>
                <MenuItem value="unmapped">Unmapped (Skipped) only</MenuItem>
                {brands.map(b => (
                  <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {selectedPageIds.size} selected · {changedCount} unsaved change(s)
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(24, 119, 242, 0.04)', borderRadius: 1 }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="bulk-brand-label">Bulk assign to brand</InputLabel>
              <Select
                labelId="bulk-brand-label"
                label="Bulk assign to brand"
                value={bulkBrand}
                onChange={(e) => setBulkBrand(e.target.value)}
                disabled={saving || selectedPageIds.size === 0}
              >
                {brands.map(b => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              disabled={!bulkBrand || selectedPageIds.size === 0 || saving}
              onClick={() => applyBulkAssignment(bulkBrand)}
            >
              Apply to selected
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={selectedPageIds.size === 0 || saving}
              onClick={() => applyBulkAssignment(SKIP_VALUE)}
            >
              Mark selected as Skip
            </Button>
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allFilteredChecked}
                      indeterminate={someFilteredChecked}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={saving || filteredPages.length === 0}
                    />
                  </TableCell>
                  <TableCell>Page</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Instagram</TableCell>
                  <TableCell>Currently assigned</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>Assign to brand</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        No pages match the current filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filteredPages.map(page => {
                  const value = pageBrandMapping[page.page_id] ?? SKIP_VALUE;
                  const isSkipped = value === SKIP_VALUE;
                  const initialValue = initialMapping[page.page_id];
                  const isChanged = value !== initialValue;
                  const currentlyAssignedTo = page.current_brand_id ? getBrandName(page.current_brand_id) : null;

                  return (
                    <TableRow
                      key={page.page_id}
                      hover
                      sx={{
                        opacity: isSkipped ? 0.6 : 1,
                        backgroundColor: isChanged ? 'rgba(255, 193, 7, 0.06)' : 'transparent'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedPageIds.has(page.page_id)}
                          onChange={(e) => handlePageCheckbox(page.page_id, e.target.checked)}
                          disabled={saving}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {page.page_name || '(no name)'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {page.page_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {page.category || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {page.ig_user_id ? (
                          <Tooltip title={`IG user id: ${page.ig_user_id}`}>
                            <Chip
                              size="small"
                              icon={<InstagramIcon fontSize="small" />}
                              label="linked"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {currentlyAssignedTo ? (
                          <Chip size="small" label={currentlyAssignedTo} color="primary" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">Not mapped</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {page.locked ? (
                          <Tooltip title="Already mapped. Reassigning a page that has fetched content isn't supported yet — it would split the data across brands. Coming soon.">
                            <Chip size="small" label="🔒 Locked" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <FormControl fullWidth size="small">
                            <Select
                              value={value}
                              onChange={(e) => handlePageBrandChange(page.page_id, e.target.value)}
                              disabled={saving}
                            >
                              <MenuItem value={SKIP_VALUE}>
                                <em>Skip — don't track this page</em>
                              </MenuItem>
                              {brands.map(b => (
                                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {changedCount} unsaved change(s)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => navigate('/brands')} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || changedCount === 0}
                startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
                sx={{ backgroundColor: '#1877F2', '&:hover': { backgroundColor: '#166FE5' } }}
              >
                {saving ? 'Saving...' : `Save Changes${changedCount > 0 ? ` (${changedCount})` : ''}`}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FacebookPagesManager;
